import { useState, useEffect } from 'react';
import { X, ArrowRight, Check, Sparkles, MapPin, Anchor, Coins, GraduationCap, MessageSquare } from 'lucide-react';

const STAGES = [
  {
    id: 'bridge',
    icon: Anchor,
    eyebrow: 'Stage 1 of 5 · Command Bridge',
    title: 'Welcome home, Admiral.',
    signal: 'The LIVE FREE Hub is now under your command. This is your central bridge for orchestrating the Nigeria · UK · USA relocation corridor.',
    highlightSelector: null,
  },
  {
    id: 'pillars',
    icon: MapPin,
    eyebrow: 'Stage 2 of 5 · Four-Pillar Array',
    title: 'Four tactical sectors.',
    signal: 'Your empire is divided into RELOCATION, REAL ESTATE, FINANCE, and LOGISTICS. Each pillar houses its own lead-capture engines and elite partner handshakes.',
    highlightSelector: '[data-testid="nav-pillar-relocation"], [data-testid="nav-pillar-real-estate"], [data-testid="nav-pillar-finance"], [data-testid="nav-pillar-logistics"]',
  },
  {
    id: 'treasury',
    icon: Coins,
    eyebrow: 'Stage 3 of 5 · Private Treasury',
    title: 'The public sees the mission. You see the gold.',
    signal: 'Behind this gate, the Treasury Doctrine reveals 50% direct to Mercy · 40% Fleet · 10% Anchor. Every signal fired through this hull routes 50% directly to you.',
    highlightSelector: '[data-testid="treasury-doctrine"]',
  },
  {
    id: 'academy',
    icon: GraduationCap,
    eyebrow: 'Stage 4 of 5 · Mastery Vault',
    title: 'Knowledge as weaponized curriculum.',
    signal: 'Your Academy holds Relocation Mastery blueprints — UK JAPA, Career Command UK, Sovereign Finance UK, Base Securing. Residual revenue while training your junior officer corps.',
    highlightSelector: null,
  },
  {
    id: 'assistant',
    icon: MessageSquare,
    eyebrow: 'Stage 5 of 5 · Sovereign Assistant',
    title: 'Your Wingman is on watch · 24/7.',
    signal: 'Command it to generate reports, audit client documents, or update the hull in real-time. Tap the gold bubble at any time.',
    highlightSelector: '[data-testid="sovereign-assistant-bubble"], [data-testid="wingman-trigger"]',
    glow: 'violet',
  },
];

const STORAGE_KEY = 'sov_admiral_tour_v1';

export default function AdmiralWalkthrough({ forceOpen = false, onClose = () => {} }) {
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(forceOpen);
  const [done, setDone] = useState(false);

  // Auto-open on first login (when forceOpen is initially true OR no completion flag found)
  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      setIdx(0);
      setDone(false);
      return;
    }
    try {
      const completed = localStorage.getItem(STORAGE_KEY);
      if (!completed) {
        const t = setTimeout(() => setOpen(true), 900);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage unavailable */ }
  }, [forceOpen]);

  // Apply spotlight class to highlighted DOM nodes
  useEffect(() => {
    if (!open) return;
    const stage = STAGES[idx];
    const targets = stage.highlightSelector
      ? Array.from(document.querySelectorAll(stage.highlightSelector))
      : [];
    targets.forEach((el) => {
      el.classList.add('sov-tour-spotlight');
      if (stage.glow === 'violet') el.classList.add('sov-tour-spotlight-violet');
    });
    return () => {
      targets.forEach((el) => {
        el.classList.remove('sov-tour-spotlight');
        el.classList.remove('sov-tour-spotlight-violet');
      });
    };
  }, [open, idx]);

  const finish = (skipped = false) => {
    try { localStorage.setItem(STORAGE_KEY, skipped ? 'skipped' : 'completed'); } catch (e) { console.error('tour persist failed:', e); }
    if (!skipped) setDone(true);
    else { setOpen(false); onClose(); }
  };

  const next = () => {
    if (idx < STAGES.length - 1) setIdx(idx + 1);
    else finish(false);
  };

  const close = () => {
    setOpen(false);
    setDone(false);
    onClose();
  };

  if (!open) return null;

  const stage = STAGES[idx];
  const Icon = stage.icon;

  return (
    <>
      {/* Obsidian overlay */}
      <div
        data-testid="admiral-walkthrough"
        className="fixed inset-0 z-[1000] bg-[#050505]/85 backdrop-blur-sm"
        style={{ animation: 'fadeIn 300ms ease-out' }}
      />

      {/* Card */}
      <div className="fixed inset-0 z-[1001] flex items-center justify-center px-6 pointer-events-none">
        <div
          data-testid={`admiral-tour-stage-${stage.id}`}
          className="sov-border bg-[#0A0A0A] p-8 md:p-10 max-w-xl w-full pointer-events-auto relative shadow-[0_0_48px_rgba(212,175,55,0.25)]"
          style={{ animation: 'fadeUp 420ms ease-out' }}
        >
          <button
            onClick={close}
            data-testid="admiral-tour-close"
            aria-label="Close tour"
            className="absolute top-4 right-4 p-2 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
          >
            <X size={16} strokeWidth={1.5} />
          </button>

          {done ? (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border-2 border-[#D4AF37] bg-[#D4AF37]/10 mb-6">
                <Check size={28} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">Tour Complete</div>
              <h2 className="text-3xl md:text-4xl font-extralight">
                The hull is yours, <span className="font-display italic text-[#D4AF37]">Admiral.</span>
              </h2>
              <p className="text-sm text-[#A3A3A3] font-light mt-4">Command at will.</p>
              <button
                onClick={close}
                data-testid="admiral-tour-finish"
                className="mt-8 px-8 py-3 bg-[#D4AF37] text-[#050505] text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-[#E5C158] transition-colors"
              >
                Take Command
              </button>
            </div>
          ) : (
            <>
              {/* Stage indicator dots */}
              <div className="flex items-center gap-1.5 mb-6">
                {STAGES.map((s, i) => (
                  <div
                    key={s.id}
                    className={`h-1 flex-1 transition-colors ${i <= idx ? 'bg-[#D4AF37]' : 'bg-[#2a2a2a]'}`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-3 mb-5">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stage.glow === 'violet' ? 'bg-[#7C3AED]/15 border border-[#7C3AED]' : 'bg-[#D4AF37]/10 border border-[#D4AF37]'}`}>
                  <Icon size={18} strokeWidth={1.5} className={stage.glow === 'violet' ? 'text-[#7C3AED]' : 'text-[#D4AF37]'} />
                </div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">{stage.eyebrow}</div>
              </div>

              <h2 className="text-3xl md:text-4xl font-extralight tracking-tight leading-[1.15] mb-5">
                {stage.title}
              </h2>
              <p className="text-base text-[#A3A3A3] font-light leading-relaxed">
                {stage.signal}
              </p>

              <div className="flex items-center justify-between gap-4 mt-10 pt-6 border-t border-[#D4AF37]/15">
                <button
                  onClick={() => finish(true)}
                  data-testid="admiral-tour-skip"
                  className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
                >
                  Skip Tour
                </button>
                <button
                  onClick={next}
                  data-testid="admiral-tour-next"
                  className="px-6 py-3 bg-[#D4AF37] text-[#050505] text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-[#E5C158] transition-colors flex items-center gap-2"
                >
                  {idx === STAGES.length - 1 ? 'Finish' : 'Next'}
                  {idx === STAGES.length - 1 ? <Check size={12} strokeWidth={2} /> : <ArrowRight size={12} strokeWidth={2} />}
                </button>
              </div>
            </>
          )}

          <div className="mt-6 text-[9px] tracking-[0.4em] uppercase text-[#666] text-center flex items-center justify-center gap-2">
            <Sparkles size={9} strokeWidth={1.5} /> Empowered by EIGOO INC
          </div>
        </div>
      </div>
    </>
  );
}
